// ─────────────────────────────────────────────
//  sheetImports — pure helpers for the "recent imported sheets" feature.
//
//  Framework-free + unit-tested. The Firestore layer (firebase/firestore.js)
//  and the UI (AssignmentImport / DailyAssignmentPage) reuse these so the
//  "keep the last N, drop anything older than D days, show it nicely" rules
//  live in one tested place — no Date math scattered across components.
// ─────────────────────────────────────────────

export const MS_PER_DAY = 24 * 60 * 60 * 1000;
export const RECENT_LIMIT = 3;
export const MAX_AGE_DAYS = 3;

// Normalize whatever `importedAt` shape a record carries into epoch ms.
// Accepts a Firestore Timestamp ({ toMillis } or { seconds }), a Date, a
// number (already ms), or an ISO string. Returns 0 when unresolvable so an
// undated record sorts last and is treated as stale.
export function importedAtMs(rec) {
  const v = rec && typeof rec === "object" && "importedAt" in rec ? rec.importedAt : rec;
  if (v == null) return 0;
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    const t = Date.parse(v);
    return Number.isNaN(t) ? 0 : t;
  }
  if (v instanceof Date) return v.getTime();
  if (typeof v.toMillis === "function") return v.toMillis();
  if (typeof v.seconds === "number") return v.seconds * 1000;
  return 0;
}

// Build the stored filename, always date-prefixed (YYYY-MM-DD_name). Strips an
// existing date prefix so re-imports don't stack prefixes; falls back to a
// stable default when the raw name is missing.
export function importFilename(rawName, dateKey) {
  const base = String(rawName || "").trim() || "assignment-sheet.xlsx";
  const stripped = base.replace(/^\d{4}-\d{2}-\d{2}[_ ]/, "");
  const day = /^\d{4}-\d{2}-\d{2}$/.test(String(dateKey || "")) ? dateKey : "";
  return day ? `${day}_${stripped}` : stripped;
}

// Reverse of importFilename: the ORIGINAL Drive file title (each day's sheet
// is its own file, e.g. "Prelim Anes Assignments 7.9.26" — effectively frozen
// once the day passes). Lets a legacy record BACKFILL its snapshot from the
// exact dated file it was imported from.
export function originalSheetTitle(filename) {
  return String(filename || "").replace(/^\d{4}-\d{2}-\d{2}[_ ]/, "").trim();
}

// The N most recent records, newest first. Pure sort + slice; safe on non-arrays.
// Global-history sources (the 🌐 list). The facility-only "Recent imports"
// list EXCLUDES these — each history is tracked separately, 3 records each.
export const GLOBAL_IMPORT_RECORD_SOURCES = ["global-sheet", "scheduled", "scheduled-internal"];
export const isGlobalImportRecord = (rec) =>
  GLOBAL_IMPORT_RECORD_SOURCES.includes(rec?.source);

export function selectRecent(list, n = RECENT_LIMIT) {
  if (!Array.isArray(list)) return [];
  return [...list]
    .sort((a, b) => importedAtMs(b) - importedAtMs(a))
    .slice(0, Math.max(0, n));
}

// True when a record is older than `days` relative to `nowMs`.
export function isStaleImport(rec, nowMs, days = MAX_AGE_DAYS) {
  const cutoff = nowMs - days * MS_PER_DAY;
  return importedAtMs(rec) < cutoff;
}

// The ids of records older than the age limit — what the cleanup task deletes.
export function staleImportIds(list, nowMs, days = MAX_AGE_DAYS) {
  if (!Array.isArray(list)) return [];
  return list.filter((r) => isStaleImport(r, nowMs, days)).map((r) => r.id).filter(Boolean);
}

// Short human label for a record's age, for the recent-list UI.
export function relativeImportTime(rec, nowMs) {
  const ms = importedAtMs(rec);
  if (!ms) return "";
  const diff = Math.max(0, nowMs - ms);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return days === 1 ? "yesterday" : `${days}d ago`;
}

// Human label for the import source tag shown as a badge.
export const SOURCE_LABELS = {
  "manual-excel": "Excel",
  "manual-sheet": "Google Sheet",
  scheduled: "Auto 1 AM",
};
export function sourceLabel(source) {
  return SOURCE_LABELS[source] || "Import";
}
